(ns app.front.view.application.committee-meetings
  (:require [app.domain.committee.model :as committee-model]
            [app.domain.inspection.model :as inspection-model]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.add-widget :as ui-add]
            [app.front.ui.collapsible :as collapsible]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.ui.select :as ui-select]
            [app.front.util :refer [for-frag]]
            [app.front.view.committees.common :as committee-common]
            [app.front.view.workspace.section :as ws-section]
            [app.front.view.workspace.subs :as ws-subs]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


(def id-for-new-meeting-item :new-meeting-item)


(rf/reg-sub ::meeting-items-being-edited
  (fn [db _]
    (-> db ::meeting-items-being-edited)))


(rf/reg-sub ::meeting-item-is-being-edited?
  :<- [::meeting-items-being-edited]
  (fn [meeting-items-being-edited [_ meeting-item-id]]
    (contains? (set meeting-items-being-edited) meeting-item-id)))


(rf/reg-sub ::meeting-items-being-saved
  (fn [db _]
    (-> db ::meeting-items-being-saved)))


(rf/reg-sub ::committee-items
  :<- [::ws-subs/workspace]
  :<- [::entities/context]
  (fn [[workspace context] _]
    (->> (entities/find-entities context {:committee-item/item (:db/id workspace)})
         (sort-by (comp :committee-meeting/date :entity/parent)))))


(rf/reg-sub ::applicable-meetings
  :<- [::ws-subs/workspace]
  :<- [::committee-items]
  (fn [[workspace committee-items] _]
    (->> workspace
         committee-model/get-workspace-applicable-meetings
         (remove (->> committee-items (map :entity/parent) set))
         distinct
         (sort-by :committee-meeting/date))))

(rf/reg-sub ::committee-options
  :<- [::applicable-meetings]
  :<- [:language]
  (fn [[applicable-meetings lang] _]
    (->> applicable-meetings
         (map :entity/parent)
         distinct
         (sort-by #(committee-model/committee-name % lang)))))

(defn get-meeting-options [applicable-meetings committee-id]
  (->> applicable-meetings
       (filter #(= committee-id (-> % :entity/parent :db/id)))))

(rf/reg-sub ::meetings-for-committee
  :<- [::applicable-meetings]
  (fn [applicable-meetings [_ committee-id]]
    (get-meeting-options applicable-meetings committee-id)))


(rf/reg-sub ::saving-disabled-for-meeting-item?
  :<- [::meeting-items-being-saved]
  (fn [meeting-items-being-saved [_ meeting-item-id]]
    (contains? (set meeting-items-being-saved) meeting-item-id)))


(rf/reg-event-db ::set-meeting-item-form-visible
  (fn [db [_ meeting-item-id visible?]]
    (update db ::meeting-items-being-edited #(if visible?
                                               (into #{meeting-item-id} %)
                                               (disj % meeting-item-id)))))


(rf/reg-event-db ::disable-saving-for-meeting-item
  (fn [db [_ meeting-item-id saving?]]
    (update db ::meeting-items-being-saved #(if saving?
                                              (into #{meeting-item-id} %)
                                              (disj % meeting-item-id)))))


(rf/reg-event-db ::close-meeting-item-form
  (fn [db [_ meeting-item-id]]
    (-> db
        (update ::meeting-items-being-edited disj meeting-item-id)
        (update ::meeting-items-being-saved disj meeting-item-id))))

(rf/reg-sub ::default-meeting
  :<- [::applicable-meetings]
  :-> first)

(rf/reg-sub ::workspace-active-deficiencies
  :<- [::ws-subs/workspace]
  (fn [workspace _]
    (when-let [execution (workspace-model/workspace-execution workspace)]
      (->> (inspection-model/phase-inspections execution)
           (mapcat inspection-model/deficiencies)
           (filter (du/in-state-fn :inspection.deficiency.state/active))))))

(defn add-if-not-contains [v coll]
  (if (contains? (set coll) v)
    coll
    (into [v] coll)))

(defn deficiency-row [{:keys [deficiency selected? on-toggle inactive?]}]
  (let [deficiency-id (:db/id deficiency)
        reference     (:inspection.deficiency/reference deficiency)
        description   (:inspection.deficiency/description deficiency)]
    [:div.deficiency-row
     {:class    (cond
                  inactive? "deficiency-row--inactive"
                  selected? "deficiency-row--selected")
      :on-click #(on-toggle deficiency-id)}
     [:div.deficiency-row__checkbox
      [cc/checkbox {:id         (str "deficiency-check-" deficiency-id)
                    :label-text ""
                    :hide-label true
                    :checked    selected?
                    :on-change  #(on-toggle deficiency-id)}]]
     [:div.deficiency-row__reference
      [ui-rt/editor-legacy {:initial-value reference}]]
     [:div.deficiency-row__description
      [ui-rt/editor-legacy {:initial-value description}]]]))

(defn deficiency-selection-list [{:keys [tr selected-ids on-toggle existing-deficiencies]}]
  (let [active-deficiencies (<sub [::workspace-active-deficiencies])
        active-id-set       (->> active-deficiencies (map :db/id) set)
        inactive-existing   (->> existing-deficiencies
                                 (remove #(contains? active-id-set (:db/id %))))
        all-deficiencies    (concat active-deficiencies inactive-existing)]
    (when (seq all-deficiencies)
      [:div.mt-6
       [:label.bx--label (tr [:committee/include-deficiencies-to-agenda])]
       [:div.deficiency-list.max-h-64.overflow-y-auto
        (for-frag [deficiency all-deficiencies]
          [deficiency-row {:deficiency deficiency
                           :selected?  (contains? selected-ids (:db/id deficiency))
                           :on-toggle  on-toggle
                           :inactive?  (not (contains? active-id-set (:db/id deficiency)))}])]])))

(defn edit-meeting-item-controls [{:keys [id tr meeting set-meeting description set-description]}]
  (let [applicable-meetings     (<sub [::applicable-meetings])
        committee-options       (<sub [::committee-options])
        current-committee-value (:entity/parent meeting)
        meeting-options         (<sub [::meetings-for-committee (:db/id current-committee-value)])
        lang                    (<sub [:language])]
    [:div.flex.flex-col.lg:flex-row
     [:div.flex.flex-col.items-stretch.flex-1.max-w-xs
      [ui-select/select {:id           (str id "-meeting-committee")
                         :data-cy      "meeting-committee"
                         :class-name   "full-width-select"
                         :label-text   (tr [:committee/meeting-type])
                         :hide-label   false
                         :required     true
                         :disabled     (nil? set-meeting)
                         :allow-empty? false
                         :value        current-committee-value
                         :values       (add-if-not-contains current-committee-value committee-options)
                         :val->str     #(str (committee-model/committee-name % lang) ", "
                                             (-> % committee-model/committee-domain-group :group/name))
                         :grouper      #(-> % committee-model/committee-domain-group :group/name)
                         :on-change    (fn [committee]
                                         (-> (get-meeting-options applicable-meetings (:db/id committee))
                                             first
                                             set-meeting))}]
      [ui-select/select {:id           (str id "-meeting-date")
                         :class-name   "full-width-select mt-2"
                         :data-cy      "meeting-date"
                         :label-text   (tr [:committee/meeting-date])
                         :hide-label   false
                         :required     true
                         :disabled     (nil? set-meeting)
                         :allow-empty? false
                         :value        meeting
                         :values       (add-if-not-contains meeting meeting-options)
                         :val->str     (comp (<sub [::date-util/date-time-fn]) :committee-meeting/date)
                         :on-change    set-meeting}]]
     [:div.flex.flex-col.items-stretch.flex-1.lg:ml-8.mt-2.lg:mt-0
      [:label.bx--label (tr [:committee-item/description])]
      [ui-rt/editor-legacy {:initial-value description
                            :id            "add-meeting-description"
                            :on-change     set-description}]]]))


(defn new-meeting-item-form [{:keys [tr phase-id]}]
  (r/with-let [*params (r/atom {:description             ""
                                :selected-deficiency-ids #{}})]
    (let [{:keys [meeting
                  description
                  selected-deficiency-ids]} @*params
          meeting                           (or meeting (<sub [::default-meeting]))
          domain-level-meetings?            (<sub [::ff/enabled? :feature-flag.id/domain-level-meetings])
          toggle-deficiency                 (fn [deficiency-id]
                                              (swap! *params update :selected-deficiency-ids
                                                     util/toggle-in-set deficiency-id))]
      [ui-add/add-widget
       {:loading? (<sub [::process/signal-processing? :process.signal/add-workspace-to-meeting])
        :buttons  [{:on-click #(>evt [::set-meeting-item-form-visible id-for-new-meeting-item false])
                    :text     (tr [:cancel])}
                   {:on-click #(>evt [::process/signal
                                      phase-id
                                      [:process.signal/add-workspace-to-meeting
                                       {:meeting-id         (-> meeting :db/id)
                                        :committee-group-id (-> meeting committee-model/meeting-group :db/id)
                                        :description        description
                                        :deficiency-ids     (vec selected-deficiency-ids)}]
                                      {:on-done [::close-meeting-item-form id-for-new-meeting-item]
                                       :notify  {:success :committee-item/added}}])
                    :text     (tr [:add])}]}
       [:div
        [edit-meeting-item-controls (util/mmap {:id              "add"
                                                :set-meeting     (fn [meeting]
                                                                   (swap! *params assoc :meeting meeting))
                                                :set-description #(swap! *params assoc :description %)}
                                               description tr meeting)]
        (when domain-level-meetings?
          [deficiency-selection-list {:tr           tr
                                      :selected-ids selected-deficiency-ids
                                      :on-toggle    toggle-deficiency}])]])))


(defn meeting-item-form [{:keys [tr phase-id]}
                         {:keys [:db/id :committee-item/deficiencies] :as item}]
  (r/with-let [initial-deficiency-ids (->> deficiencies (map :db/id) set)
               *params                (r/atom {:description             (or (:committee-item/description item) "")
                                               :selected-deficiency-ids initial-deficiency-ids})]
    (let [{:keys [description
                  selected-deficiency-ids]} @*params
          meeting                           (:entity/parent item)
          domain-level-meetings?            (<sub [::ff/enabled? :feature-flag.id/domain-level-meetings])
          toggle-deficiency                 (fn [deficiency-id]
                                              (swap! *params update :selected-deficiency-ids
                                                     util/toggle-in-set deficiency-id))
          has-changes?                      (or (not= description (:committee-item/description item))
                                                (not= selected-deficiency-ids initial-deficiency-ids))]
      [:div
       [:div.p-8.mb-8.bg-ui-blue-10.border-2.border-solid.border-ui-blue-100.rounded-md.shadow-lg.animation-appear-slide-down
        {:data-cy "edit-meeting"}
        [edit-meeting-item-controls (util/mmap {:id              (str "edit-" id)
                                                :set-description #(swap! *params assoc :description %)}
                                               tr meeting description)]
        (when domain-level-meetings?
          [deficiency-selection-list {:tr                    tr
                                      :selected-ids          selected-deficiency-ids
                                      :on-toggle             toggle-deficiency
                                      :existing-deficiencies deficiencies}])
        [:div.flex.flex-row.justify-end.mt-8.lg:mt-4
         (cc/button {:on-click #(>evt [::set-meeting-item-form-visible id false])
                     :kind     :tertiary}
                    (tr [:cancel]))
         (if (<sub [::saving-disabled-for-meeting-item? id])
           [cc/inline-loading
            {:description (tr [:saving])
             :class-name  "ml-4"}]
           (cc/button {:on-click   (fn [_]
                                     (>evt [::disable-saving-for-meeting-item id true])
                                     (>evt [::process/signal
                                            phase-id
                                            [:process.signal/edit-committee-meeting-item
                                             {:description        description
                                              :meeting-item-id    id
                                              :committee-group-id (-> item committee-model/meeting-item-group :db/id)
                                              :deficiency-ids     (vec selected-deficiency-ids)}]
                                            {:on-done [::close-meeting-item-form id]
                                             :notify  {:success (tr [:changes-saved])}}]))
                       :kind       :primary
                       :class-name "ml-2"
                       :disabled   (not has-changes?)}
                      (tr [:save-changes])))]]])))


(defn add-to-meeting-widget [{:keys [tr phase-id]}]
  (if (<sub [::meeting-item-is-being-edited? id-for-new-meeting-item])
    [new-meeting-item-form (util/mmap phase-id tr)]
    (cc/button {:on-click         #(>evt [::set-meeting-item-form-visible id-for-new-meeting-item true])
                :icon             i/plus
                :icon-description ""
                :kind             :tertiary}
               (tr [:committee-meeting/add-workspace-to-meeting]))))


(defn status-indicator-layout [tr state class icon]
  [:div.flex.flex-row.items-center.mr-4.mt-4.lg:mt-6.mb-4.ml-4
   {:class class}
   [:span.uppercase (tr [state])]
   (cc/icon {:icon        icon
             :width       "14"
             :class-name  "ml-3"
             :fill        "currentcolor"
             :description ""
             :aria-hidden true})])


(defn- status-indicator [tr domain-level-meetings? meeting-state item-state]
  (if domain-level-meetings?
    (cond
      (= item-state :committee-item.state/postponed)
      [status-indicator-layout tr :committee-item.selected-action/move-to-next "text-gray-600" i/clock]

      (= meeting-state :committee-meeting.state/cancelled)
      [status-indicator-layout tr meeting-state "text-gray-600" i/remove]

      (= meeting-state :committee-meeting.state/passed)
      [status-indicator-layout tr :committee-meeting/completed "text-ui-green" i/check])
    (let [icon (committee-common/item-status->icon item-state)]
      (case item-state
        :committee-item.state/done
        [status-indicator-layout tr item-state "text-ui-green" icon]
        :committee-item.state/postponed
        [status-indicator-layout tr item-state "text-gray-600" icon]
        :committee-item.state/canceled
        [status-indicator-layout tr item-state "text-gray-600" icon]
        nil))))

(defn item-deficiencies-display [{:keys [tr deficiencies]}]
  (when (seq deficiencies)
    (r/with-let [*open? (r/atom false)]
      [:div.mt-2.mx-4.pb-4
       [collapsible/collapsible
        {:open?     @*open?
         :on-toggle #(swap! *open? not)
         :variant   :border
         :header    [:div.flex-1.py-2.px-3.pr-8.text-sm.font-semibold
                     (tr [:committee/associated-deficiencies])
                     " (" (count deficiencies) ")"]}
        [:div.deficiency-list.px-4.py-2
         (for-frag [deficiency deficiencies]
           [:div.deficiency-display
            [:div.deficiency-row__reference
             [ui-rt/editor-legacy {:initial-value (:inspection.deficiency/reference deficiency)}]]
            [:div.deficiency-row__description
             [ui-rt/editor-legacy {:initial-value (:inspection.deficiency/description deficiency)}]]])]]])))


(rf/reg-sub ::add-permitted?
  :<- [::applicable-meetings]
  (fn [applicable-meetings [_ phase-id]]
    (and (seq applicable-meetings)
         (<sub [::process/signal-permitted?
                phase-id
                [:process.signal/add-workspace-to-meeting
                 {:committee-group-id (-> applicable-meetings first committee-model/meeting-group :db/id)}]]))))

(defn- meeting-item-row [{:keys [tr lang ctx phase-entity domain-level-meetings?]}
                         {:committee-item/keys [description deficiencies] :as item}]
  (let [{:committee-meeting/keys [date]
         :as                     meeting} (:entity/parent item)
        meeting-state                     (:domain/state meeting)
        committee                         (:entity/parent meeting)
        committee-name                    (committee-model/committee-name committee lang)]
    (if (<sub [::meeting-item-is-being-edited? (:db/id item)])
      [meeting-item-form ctx item]
      (let [format-date-time (<sub [::date-util/date-time-fn])
            item-state       (:domain/state item)]
        [:div.bg-gray-200.mb-1.border-solid.border-l-4
         {:class   (if domain-level-meetings?
                     (case meeting-state
                       :committee-meeting.state/passed "border-ui-green"
                       :committee-meeting.state/cancelled "border-gray-400"
                       "border-ui-night-sky")
                     (case item-state
                       :committee-item.state/done "border-ui-green"
                       :committee-item.state/postponed "border-gray-400"
                       :committee-item.state/canceled "border-gray-400"
                       "border-ui-night-sky"))
          :data-cy "committee-meeting-item"}
         [:div.flex.flex-row
          [:div.flex-1.flex.flex-col.md:flex-row.leading-snug.items-start
           [:div.flex-1.flex.flex-col.lg:flex-row.pt-4.lg:pb-4.px-4.lg:px-12
            [:div.flex.flex-col.mr-4.w-64
             (let [meeting-group (committee-model/meeting-group meeting)
                   content       [:div.font-bold (str (tr [:committee/committee-name-meeting] [committee-name])
                                                      ", "
                                                      (:group/name meeting-group))]]
               (cond
                 (and domain-level-meetings?
                      (-> committee :granted-permissions
                          ((some-fn :permission.committee/admin :permission.committee/read))))
                 (cc/link
                   {:href (href/href :view/meetings {:group-id (:db/id meeting-group)})}
                   content)

                 (-> committee :granted-permissions :permission.committee/admin)
                 (cc/link
                   {:href (href/href :view/committee {:committee-id (:db/id committee)})}
                   content)

                 :else
                 content))
             [:div (format-date-time date)]]
            [:div.flex-1.mt-4.lg:mt-0
             [ui-rt/editor-legacy {:initial-value description
                                   :key           (str "meeting-item-description-" (:db/id item))
                                   :id            (str "meeting-item-description-" (:db/id item))}]]]
           [status-indicator tr domain-level-meetings? meeting-state item-state]
           (let [edit-signal [:process.signal/edit-committee-meeting-item
                              {:meeting-item-id    (:db/id item)
                               :committee-group-id (-> item committee-model/meeting-item-group :db/id)}]]
             (when (<sub [::process/signal-permitted? phase-entity edit-signal])
               [cc/button
                {:icon             i/pen
                 :icon-description (tr [:edit])
                 :class-name       "lg:mt-3"
                 :data-cy          "edit-committee-meeting-item"
                 :kind             :ghost
                 :on-click         #(>evt [::set-meeting-item-form-visible (:db/id item) true])}
                (tr [:edit])]))
           (let [remove-signal [:process.signal/remove-workspace-from-meeting
                                {:meeting-item-id    (:db/id item)
                                 :committee-group-id (-> item committee-model/meeting-item-group :db/id)}]]
             (when (<sub [::process/signal-permitted? phase-entity remove-signal])
               (if (<sub [::process/signal-processing? :process.signal/remove-workspace-from-meeting])
                 [:div.mt-2 (cc/button-skeleton)]
                 [ui/modal-confirmation-button
                  {:tr                  tr
                   :id                  (str "remove-item-from-meeting-" (:db/id item))
                   :modal-title         (tr [:committee-item/remove-from-meeting-confirm-title])
                   :trigger-button-opts {:icon             i/remove
                                         :kind             :ghost
                                         :data-cy          "remove-item-from-meeting"
                                         :class-name       "lg:mt-3"
                                         :icon-description ""}
                   :trigger-button-text (tr [:committee-item/remove-from-meeting])
                   :no-button-text      (tr [:no])
                   :yes-button-text     (tr [:yes])
                   :on-yes-click        #(>evt [::process/signal
                                                phase-entity
                                                remove-signal
                                                {:notify {:success :committee-item/removed}}])}
                  [:p (tr [:committee-item/remove-from-meeting-confirm-content])]
                  [:div (tr [:committee/committee-name-meeting] [committee-name])]
                  [:div (format-date-time date)]])))]]
         (when domain-level-meetings?
           [item-deficiencies-display {:tr           tr
                                       :deficiencies deficiencies}])]))))


(defn- meeting-items-group [{:keys [tr heading items] :as opts}]
  (when (seq items)
    [:div.mt-4
     [:h4.font-bold.text-base.mb-2 (tr [heading])]
     (for-frag [item items]
       [meeting-item-row opts item])]))


(defn meetings-section [{:keys [phase-entity]}]
  (let [committee-items        (<sub [::committee-items])
        tr                     (<sub [:tr])
        lang                   (<sub [:language])
        ctx                    (util/mmap {:phase-id (:db/id phase-entity)} tr)
        add-permitted?         (<sub [::add-permitted? (:db/id phase-entity)])
        domain-level-meetings? (<sub [::ff/enabled? :feature-flag.id/domain-level-meetings])
        row-opts               (util/mmap {:phase-entity           phase-entity
                                           :domain-level-meetings? domain-level-meetings?}
                                          tr lang ctx)]
    (when (or (seq committee-items)
              add-permitted?)
      [ws-section/section
       phase-entity
       :committee-meetings-section
       {:tr      tr
        :data-cy "meeting-section"}
       (if domain-level-meetings?
         :committee/meetings-section-title-domain-level
         :committee/meetings-section-title)
       true
       [:div
        (when add-permitted?
          [:<>
           [:p (tr [:committee-meeting/add-workspace-to-meeting-description])]
           [add-to-meeting-widget ctx]])
        (if domain-level-meetings?
          (let [grouped (group-by (comp :domain/state :entity/parent) committee-items)]
            [:div.mb-4
             {:data-cy "meetings"}
             [meeting-items-group (assoc row-opts
                                         :heading :committee-meeting/upcoming-meetings-heading
                                         :items (get grouped :committee-meeting.state/active))]
             [meeting-items-group (assoc row-opts
                                         :heading :committee-meeting/past-meetings-heading
                                         :items (get grouped :committee-meeting.state/passed))]
             [meeting-items-group (assoc row-opts
                                         :heading :committee-meeting/cancelled-meetings-heading
                                         :items (get grouped :committee-meeting.state/cancelled))]])
          [:div.mt-4.mb-4
           {:data-cy "meetings"}
           (for-frag [item committee-items]
             [meeting-item-row row-opts item])])]])))
